import { z } from "zod";

export const UpdatedPropertySchema = z.object({
    property_type: z.string().min(1, "Property type is required"),
    name: z.string().min(1, "Name is required"),
    preferred_currency: z.string().min(1, "Preferred currency is required"),
    time_zone: z.string().min(1, "Time zone is required"),
    primary_email: z.string().email("Invalid email format").min(1, "Primary email is required"),
    primary_phone_country_code: z.nullable(z.string()).optional(),
    primary_phone: z.nullable(z.string()),
    postal_code: z.nullable(z.string()),
    country: z.nullable(z.string()),
    state: z.nullable(z.string()).optional(),
    city: z.nullable(z.string()).optional(),
    street_address: z.nullable(z.string()),
    longitude: z.nullable(z.string()).optional(),
    latitude: z.nullable(z.string()).optional(),
    description: z.nullable(z.string()).optional(),
    term_condition: z.string().nullable().optional(),
    cancellation_policy: z.string().nullable().optional(),
    website: z.nullable(z.string()),
    email_autofill: z.preprocess(
        (val) => (val === "" ? null : val), // Convert empty string to null
        z.union([z.string().email("Invalid email format"), z.null()]).optional()
    ),
    cc_emails: z.array(z.string()).optional().nullable().superRefine((emails, ctx) => {
        emails?.forEach((email, index) => {
            if (!z.string().email().safeParse(email).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `Invalid email format at position ${index + 1}: "${email}"`,
                    path: [index],
                });
            }
        });
    }),
    accounting_email: z.preprocess(
        (val) => (val === "" ? null : val), // Convert empty string to null
        z.union([z.string().email("Invalid email format"), z.null()]).optional()
    ),
    mobile_number_country_code: z.nullable(z.string()).optional(),
    mobile_number: z.nullable(z.string()),
    // enable_cvv: value is 1 or 0
    enable_cvv: z.nullable(z.number().int()),
    region_id: z.string().uuid().nullable().optional(),
    // Group whitelabel yang menaungi property ini — 1 property hanya boleh punya 1 group.
    // null berarti "tidak ada group" (lihat alert di list property untuk kasus ini).
    group_id: z.string().uuid().nullable().optional(),
    // Logo validation
    logo: z
        .any()
        .nullable()
        .refine((file) => !file || (file instanceof File && file.size <= 2 * 1024 * 1024), {
            message: "Logo must be an image file and less than 2MB",
        })
        .refine((file) => !file || file.type.startsWith("image/"), {
            message: "Logo must be an image file (JPG, PNG, etc.)",
        }),
    checkin_time_start: z.nullable(z.string()).optional(),
    checkin_time_end: z.nullable(z.string()).optional(),
    checkout_time_start: z.nullable(z.string()).optional(),
    checkout_time_end: z.nullable(z.string()).optional(),
}).superRefine((data, ctx) => {
    if (data.enable_cvv !== null && data.enable_cvv > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid value for enable_cvv",
            path: ["enable_cvv"],
        });
    }
});

export type UpdatePropertyType = z.infer<typeof UpdatedPropertySchema>;